import { SCHEDULE_TIME_OPTIONS } from "@/components/news-alerts/newsAlertsHelpers";
import { Label } from "@/components/ui/field";
import ScheduleTimeCombobox from "@/components/ui/molecules/schedule-time-combobox/ScheduleTimeCombobox";
import TimezoneCombobox from "@/components/ui/molecules/timezone-combobox/TimezoneCombobox";
import { resolveLocalTimezoneValue } from "@/components/ui/molecules/timezone-combobox/timezoneComboboxHelpers";
import useAvailableTimezones from "@/components/ui/molecules/timezone-combobox/useAvailableTimezones";
import { JollySelect, SelectItem } from "@/components/ui/select";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import type { BriefingDefinitionDeliverySchedule } from "@/data-access/scheduled-briefings/queries/briefing-definition-queries";

const BRIEFING_CREATE_FREQUENCY_OPTIONS = [
  { id: "DAILY", label: "Every day" },
  { id: "WEEKDAYS", label: "Every weekday" },
  { id: "MONDAY", label: "Every Monday" },
  { id: "TUESDAY", label: "Every Tuesday" },
  { id: "WEDNESDAY", label: "Every Wednesday" },
  { id: "THURSDAY", label: "Every Thursday" },
  { id: "FRIDAY", label: "Every Friday" },
  { id: "SATURDAY", label: "Every Saturday" },
  { id: "SUNDAY", label: "Every Sunday" },
  { id: "MONTHLY", label: "Every month" },
] as const;

export interface BriefingCreateScheduleSectionProps {
  scheduleFrequency: BriefingDefinitionDeliverySchedule | "";
  scheduleTime: string;
  timezone: TimezoneChoices | "";
  onScheduleFrequencyChange: (
    value: BriefingDefinitionDeliverySchedule | "",
  ) => void;
  onScheduleTimeChange: (value: string) => void;
  onTimezoneChange: (value: TimezoneChoices | "") => void;
  /** Called when the browser timezone fills an empty timezone. */
  onTimezoneAutoSelect: (value: TimezoneChoices) => void;
  isDisabled?: boolean;
}

const BriefingCreateScheduleSection = ({
  scheduleFrequency,
  scheduleTime,
  timezone,
  onScheduleFrequencyChange,
  onScheduleTimeChange,
  onTimezoneChange,
  onTimezoneAutoSelect,
  isDisabled = false,
}: BriefingCreateScheduleSectionProps) => {
  const { availableTimezones, isFetchingAvailableTimezones } =
    useAvailableTimezones({
      timezone,
      onTimezoneChange: onTimezoneAutoSelect,
    });

  // A cleared timezone falls back to the browser timezone, then US_EASTERN, so
  // the field shows the value save will send.
  const getClearedTimezone = () =>
    resolveLocalTimezoneValue(availableTimezones) ??
    (availableTimezones.some((option) => option.value === "US_EASTERN")
      ? "US_EASTERN"
      : "");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <Label variant="secondary" size="md">
          Frequency
        </Label>
        <JollySelect
          aria-label="Frequency"
          placeholder="Select"
          size="sm"
          items={BRIEFING_CREATE_FREQUENCY_OPTIONS}
          selectedKey={scheduleFrequency || undefined}
          onSelectionChange={(key) => {
            if (key == null) return;
            onScheduleFrequencyChange(
              key as BriefingDefinitionDeliverySchedule,
            );
          }}
          isDisabled={isDisabled}
          className="[&_button]:h-9 [&_button]:rounded-sm [&_button]:text-sm"
        >
          {(option) => <SelectItem id={option.id}>{option.label}</SelectItem>}
        </JollySelect>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,5fr)_minmax(0,9fr)]">
        <ScheduleTimeCombobox
          clearable={true}
          label="Send time"
          labelVariant="secondary"
          labelSize="md"
          options={SCHEDULE_TIME_OPTIONS}
          onValueChange={onScheduleTimeChange}
          value={scheduleTime}
          disabled={isDisabled}
          className="min-w-0 gap-0"
        />
        <TimezoneCombobox
          clearable={true}
          label="Timezone"
          labelVariant="secondary"
          labelSize="md"
          options={availableTimezones}
          onValueChange={(value) =>
            onTimezoneChange(
              (value || getClearedTimezone()) as TimezoneChoices | "",
            )
          }
          value={timezone}
          isPending={isFetchingAvailableTimezones}
          disabled={isDisabled}
          className="min-w-0 gap-0"
        />
      </div>
    </div>
  );
};

export default BriefingCreateScheduleSection;
